import { useCallback, useEffect, useState } from "react";
import { api, extractAxiosError } from "@/lib/api";
import { DeleteConfirmModal } from "@/components/DeleteConfirmModal";
import { AdminPoolProfileDialog } from "@/components/admin/AdminEntityProfileDialog";
import { AdminPanelShell } from "@/components/admin/AdminPanelShell";
import { RefreshCw, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";

export function AdminPoolsPage() {
    const [pools, setPools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [profilePoolId, setProfilePoolId] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/pools", { params: { limit: 300 } });
            setPools(data.pools || []);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load pools"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setBusy(true);
        try {
            await api.delete(`/admin/pools/${deleteTarget.id}`);
            toast.success("Pool deleted");
            setDeleteTarget(null);
            load();
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to delete pool"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
        <AdminPanelShell
            title="All pools"
            count={pools.length}
            loading={loading}
            empty={!loading && pools.length === 0}
            emptyMessage="No pools on the platform yet."
            testId="admin-pools"
            headerExtra={
                <button type="button" onClick={load} className="btn-ghost inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs">
                    <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </button>
            }
        >
            <table className="w-full text-left text-sm">
                <thead className="sticky top-0 z-10 bg-[#262626] text-[10px] uppercase tracking-wide text-white/40">
                    <tr>
                        <th className="px-4 py-2.5 font-medium">Pool</th>
                        <th className="px-4 py-2.5 font-medium">Organization</th>
                        <th className="px-4 py-2.5 font-medium">Members</th>
                        <th className="px-4 py-2.5 font-medium">Cadence</th>
                        <th className="px-4 py-2.5 font-medium text-right">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                    {pools.map((pool) => (
                        <tr key={pool.id} className="hover:bg-white/[0.02]" data-testid={`admin-pool-row-${pool.id}`}>
                            <td className="px-4 py-3">
                                <p className="font-medium text-white/90">{pool.name}</p>
                                <p className="mt-0.5 font-mono text-[10px] text-white/35">{pool.id}</p>
                            </td>
                            <td className="px-4 py-3">
                                <p className="text-white/75">{pool.org_name || "—"}</p>
                                <p className="mt-0.5 text-[10px] uppercase text-white/35">{pool.org_plan || "free"}</p>
                            </td>
                            <td className="px-4 py-3 font-mono tabular-nums">{pool.member_count}</td>
                            <td className="px-4 py-3 capitalize text-white/60">{pool.cadence || "—"} · {pool.currency || "—"}</td>
                            <td className="px-4 py-3 text-right">
                                <div className="inline-flex items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => setProfilePoolId(pool.id)}
                                        className="inline-flex h-8 items-center justify-center gap-1 rounded-md px-2 text-xs text-white/55 hover:bg-white/[0.06] hover:text-white"
                                        data-testid={`view-pool-profile-${pool.id}`}
                                    >
                                        <Layers className="h-3.5 w-3.5" /> Profile
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setDeleteTarget(pool)}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white/40 hover:bg-rose-500/10 hover:text-rose-400"
                                        aria-label={`Delete pool ${pool.name}`}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </AdminPanelShell>

        <AdminPoolProfileDialog poolId={profilePoolId} open={!!profilePoolId} onClose={() => setProfilePoolId(null)} />

        <DeleteConfirmModal
            open={!!deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onConfirm={confirmDelete}
            busy={busy}
            title={`Delete pool "${deleteTarget?.name}"?`}
            description="Permanently removes this pool and cascades payouts, revenue events, and related data."
        >
            {deleteTarget?.created_at && (
                <p className="text-xs text-white/45">Created {new Date(deleteTarget.created_at).toLocaleString()}</p>
            )}
        </DeleteConfirmModal>
        </>
    );
}
